package info.rezident.app.data

import info.rezident.app.core.FirebaseAi
import kotlinx.coroutines.await
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

object AiRepository {
    private const val DEFAULT_MODEL: String = "gemini-2.5-flash"
    private val initMutex: Mutex = Mutex()
    private var initialized: Boolean = false

    private suspend fun ensureInitialized() {
        if (initialized) return
        initMutex.withLock {
            if (initialized) return
            FirebaseAi.initFirebaseAi().await()
            initialized = true
        }
    }

    suspend fun generateText(prompt: String, modelName: String = DEFAULT_MODEL): String {
        ensureInitialized()
        return FirebaseAi.generateText(modelName, prompt).await()
    }

    /**
     * Match type for the 4 categories from cloud function analysis
     */
    enum class MatchType {
        PERFECT_HIGH,    // Both user and specialty scored high (4-5) - this is a PRO
        PERFECT_LOW,     // Both user and specialty scored low (1-2) - this is a PRO
        DIFFERENT_HIGH,  // User scored high but specialty scored low - this is a CON
        DIFFERENT_LOW    // User scored low but specialty scored high - this is a CON
    }

    /**
     * Data class for a single question to explain with match type context
     */
    data class QuestionToExplain(
        val specialty: String,
        val questionId: String,
        val questionText: String,
        val isPro: Boolean,
        val matchType: MatchType = if (isPro) MatchType.PERFECT_HIGH else MatchType.DIFFERENT_HIGH
    )

    /**
     * Generate batch explanations for multiple specialties, pros and cons in a SINGLE AI request.
     * This avoids quota exhaustion by making one request for all specialties instead of many.
     *
     * @param questionsBySpecialty Map of specialty name to list of questions
     * @return Map of "specialty:questionId" to generated explanation text
     */
    suspend fun generateBatchProConsExplanationsForAllSpecialties(
        questionsBySpecialty: Map<String, List<QuestionToExplain>>,
        modelName: String = DEFAULT_MODEL
    ): Map<String, String> {
        if (questionsBySpecialty.isEmpty()) return emptyMap()

        val prompt = buildString {
            appendLine("Ești consilier de carieră medicală. Generează explicații scurte (1-2 propoziții) pentru punctele forte și slabe ale mai multor specialități medicale.")
            appendLine()
            appendLine("CONTEXT: Fiecare întrebare are un TIP DE POTRIVIRE care indică relația dintre răspunsul utilizatorului și profilul specialității:")
            appendLine("- PERFECT_HIGH: Atât utilizatorul, cât și specialitatea au scor RIDICAT (4-5) pe acest aspect → PUNCT FORTE")
            appendLine("- PERFECT_LOW: Atât utilizatorul, cât și specialitatea au scor SCĂZUT (1-2) pe acest aspect → PUNCT FORTE")
            appendLine("- DIFFERENT_HIGH: Utilizatorul are scor RIDICAT, dar specialitatea are scor SCĂZUT → PUNCT SLAB")
            appendLine("- DIFFERENT_LOW: Utilizatorul are scor SCĂZUT, dar specialitatea are scor RIDICAT → PUNCT SLAB")
            appendLine()
            appendLine("REGULI DE GENERARE:")
            appendLine("- Pentru PERFECT_HIGH: Explică cum caracteristica/abilitatea ta ridicată se aliniază perfect cu cerințele ridicate ale specialității")
            appendLine("  Exemple: 'Abilitatea ta ridicată de X este esențială în Y, unde se lucrează intensiv cu Z', 'Pasiunea ta pentru X te va ajuta să excelezi în Y'")
            appendLine("- Pentru PERFECT_LOW: Explică cum absența/nivelul scăzut al acestei caracteristici la tine se potrivește cu cerințele scăzute ale specialității")
            appendLine("  Exemple: 'Specialitatea necesită nivel scăzut de X, ceea ce se aliniază cu profilul tău', 'Nu vei avea nevoie de X în această specialitate'")
            appendLine("- Pentru DIFFERENT_HIGH: Explică cum nivelul tău ridicat de X poate fi în conflict cu cerințele scăzute ale specialității")
            appendLine("  Exemple: 'Abilitatea ta ridicată de X ar putea fi subutilizată în Y', 'Specialitatea nu oferă multe oportunități pentru X, deși tu excelezi la asta'")
            appendLine("- Pentru DIFFERENT_LOW: Explică cum nivelul tău scăzut de X poate fi o provocare față de cerințele ridicate ale specialității")
            appendLine("  Exemple: 'Specialitatea necesită nivel ridicat de X, ceea ce poate fi o provocare pentru tine', 'Vei avea nevoie să dezvolți X pentru a reuși în Y'")
            appendLine()
            appendLine("STIL:")
            appendLine("- Răspunde EXCLUSIV în limba română")
            appendLine("- Fii concis și specific (maxim 2 propoziții per explicație)")
            appendLine("- Variază formulările pentru a suna natural")
            appendLine("- Nu include introduceri sau concluzii generice")
            appendLine()
            appendLine("Format răspuns: Pentru fiecare întrebare, scrie pe o linie nouă în formatul:")
            appendLine("SPECIALITATE|ID_ÎNTREBARE: Explicația ta aici")
            appendLine()

            // Process each specialty
            questionsBySpecialty.forEach { (specialty, questions) ->
                appendLine("=== SPECIALITATE: $specialty ===")
                appendLine()

                // Group by match type for clarity
                val perfectHigh = questions.filter { it.matchType == MatchType.PERFECT_HIGH }
                val perfectLow = questions.filter { it.matchType == MatchType.PERFECT_LOW }
                val differentHigh = questions.filter { it.matchType == MatchType.DIFFERENT_HIGH }
                val differentLow = questions.filter { it.matchType == MatchType.DIFFERENT_LOW }

                if (perfectHigh.isNotEmpty()) {
                    appendLine("PUNCTE FORTE (PERFECT_HIGH - amândoi aveți scor ridicat):")
                    perfectHigh.forEach { q ->
                        appendLine("${q.questionId}. ${q.questionText}")
                    }
                    appendLine()
                }

                if (perfectLow.isNotEmpty()) {
                    appendLine("PUNCTE FORTE (PERFECT_LOW - amândoi aveți scor scăzut):")
                    perfectLow.forEach { q ->
                        appendLine("${q.questionId}. ${q.questionText}")
                    }
                    appendLine()
                }

                if (differentHigh.isNotEmpty()) {
                    appendLine("PUNCTE SLABE (DIFFERENT_HIGH - tu ai scor ridicat, specialitatea are scor scăzut):")
                    differentHigh.forEach { q ->
                        appendLine("${q.questionId}. ${q.questionText}")
                    }
                    appendLine()
                }

                if (differentLow.isNotEmpty()) {
                    appendLine("PUNCTE SLABE (DIFFERENT_LOW - tu ai scor scăzut, specialitatea are scor ridicat):")
                    differentLow.forEach { q ->
                        appendLine("${q.questionId}. ${q.questionText}")
                    }
                    appendLine()
                }
            }

            appendLine("Acum generează explicațiile în formatul specificat (SPECIALITATE|ID: Explicație):")
        }

        val response = generateText(prompt, modelName)

        // Parse the response to extract explanations
        return parseExplanationsFromResponseWithSpecialties(response, questionsBySpecialty)
    }

    /**
     * Parse AI response with multiple specialties to extract explanations
     * Format: "SPECIALITATE|ID: Explanation"
     * Returns map with key "specialty:questionId"
     */
    private fun parseExplanationsFromResponseWithSpecialties(
        response: String,
        questionsBySpecialty: Map<String, List<QuestionToExplain>>
    ): Map<String, String> {
        val result = mutableMapOf<String, String>()
        val lines = response.lines()

        for (line in lines) {
            // Look for pattern: "SPECIALTY|ID: Explanation"
            val colonIndex = line.indexOf(':')
            if (colonIndex > 0) {
                val keyPart = line.substring(0, colonIndex).trim()
                val explanation = line.substring(colonIndex + 1).trim()

                // Try to split by pipe
                val pipeIndex = keyPart.indexOf('|')
                if (pipeIndex > 0) {
                    val specialty = keyPart.substring(0, pipeIndex).trim()
                    val idPart = keyPart.substring(pipeIndex + 1).trim()
                    val numericId = idPart.replace(".", "").trim()

                    // Find matching question
                    val questions = questionsBySpecialty[specialty]
                    val matchingQuestion = questions?.find { it.questionId == numericId }

                    if (matchingQuestion != null && explanation.isNotBlank()) {
                        // Use composite key: "specialty:questionId"
                        result["${specialty}:${numericId}"] = explanation
                    }
                }
            }
        }

        return result
    }
}